import { initHorizontalScroll } from './horizontal-scroll.js';
import { processScroll, mobileScroll } from './content.js';
import { initScrollbar } from './scrollbar.js';
import { initHorizontalSwipe } from './horizontal-swipe.js';

// dynasor由来の横移動へFFDの端の抵抗設定を渡す。
// 既存の矢印・カウンターと、本文が画面に収まるかの判定を担当。
export function initProcessScroll(root, lenis, reducedMotion, { scroller = window, desktop, createScrollbar = initScrollbar } = {}) {
  const clip = root.querySelector('.process');
  const inner = root.querySelector('.approach-inner');
  const heading = inner.querySelector(':scope > .section-head');
  const track = root.querySelector('[data-horizontal-track]');
  const cards = [...track.querySelectorAll('article')];
  const controls = root.querySelector('.slide-controls');
  const count = root.querySelector('[data-process-count]');
  const previous = root.querySelector('.process-prev');
  const next = root.querySelector('.process-next');
  const cleanups = [];
  let horizontal;
  let scrollbar;

  const listen = (element, type, callback) => {
    element.addEventListener(type, callback);
    cleanups.push(() => element.removeEventListener(type, callback));
  };
  const getState = () => horizontal?.getState();
  const updateControls = () => {
    const state = getState();
    if (!state) return;
    const { distance, position } = state;
    const current = Math.min(Math.floor((position + .001) / Math.max(cards[0].offsetWidth, 1)) + 1, cards.length);
    const label = `${String(current).padStart(2, '0')} / ${String(cards.length).padStart(2, '0')}`;
    if (count.textContent !== label) count.textContent = label;
    previous.disabled = position <= 0;
    next.disabled = position >= distance;
  };
  const prepareScrollbar = () => {
    if (!scrollbar) scrollbar = createScrollbar(clip, {
      axis: 'x', label: '制作での取り組み。左右の矢印キーでも移動できます',
      onInteract: () => lenis.scrollTo(lenis.actualScroll, { immediate: true }),
    });
    scrollbar.refresh();
  };
  const refresh = () => {
    // PCの低い画面・拡大表示では、従来どおり通常フローで読む。
    root.classList.add('is-horizontal');
    controls.hidden = false;
    const viewportHeight = scroller === window ? window.innerHeight : scroller.clientHeight;
    // 次の見出しが画面下へ続く場合も、取り組み本文が収まれば横移動を維持する。
    const processHeight = clip.getBoundingClientRect().bottom - inner.getBoundingClientRect().top;
    const fits = processHeight <= viewportHeight + 1 && track.scrollHeight <= clip.clientHeight + 1;
    // 5枚が一列に収まる大画面では、横移動とそのための固定・待機を解除する。
    const overflows = track.scrollWidth > clip.clientWidth + 1;
    if (overflows && ((desktop && !desktop.matches) || fits)) {
      prepareScrollbar();
      if (horizontal) horizontal.refresh();
      else horizontal = initHorizontalScroll(root, {
        lenis,
        scroller,
        viewport: scrollbar.scrollElement,
        stickyElement: inner,
        // 短いスマホ画面では本文下端まで縦に読んでから横移動を始める。
        stickyTop: () => desktop && !desktop.matches
          ? Math.min(0, (scroller === window ? window.innerHeight : scroller.clientHeight)
            - (clip.getBoundingClientRect().bottom - inner.getBoundingClientRect().top))
          : 0,
        startHoldVh: processScroll.startHoldVh,
        // 先頭は既存の開始待機を使い、2枚目以降に保持区間を足す。
        holdPositions: () => desktop && !desktop.matches
          ? cards.slice(1).map((card) => card.offsetLeft - cards[0].offsetLeft) : [],
        holdDistance: () => (scroller === window ? window.innerHeight : scroller.clientHeight) * mobileScroll.cardHoldVh / 100,
        edgeWidth: () => cards[0].offsetWidth * processScroll.edgeCards,
        edgeResistance: processScroll.edgeResistance,
      });
      updateControls();
    } else {
      horizontal?.destroy();
      horizontal = null;
      scrollbar?.destroy();
      scrollbar = null;
      root.classList.remove('is-horizontal');
      controls.hidden = true;
      lenis.resize();
    }
  };
  const move = (direction) => {
    const state = getState();
    if (!state) return;
    const { position } = state;
    lenis.scrollTo(horizontal.getScrollPosition(position + direction * cards[0].offsetWidth), {
      duration: .65,
      immediate: reducedMotion.matches,
    });
  };
  cleanups.push(initHorizontalSwipe(clip, {
    enabled: () => Boolean(desktop && !desktop.matches), controller: () => horizontal,
    positions: () => cards.map((card) => card.offsetLeft - cards[0].offsetLeft), lenis, reducedMotion,
  }));
  listen(previous, 'click', () => move(-1));
  listen(next, 'click', () => move(1));
  listen(root, 'keydown', (event) => {
    if (!getState() || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
    event.preventDefault();
    move(event.key === 'ArrowRight' ? 1 : -1);
  });
  listen(window, 'resize', refresh);
  listen(window, 'load', refresh);
  if (desktop) listen(desktop, 'change', refresh);
  lenis.on('scroll', updateControls);
  const observer = new ResizeObserver(refresh);
  observer.observe(heading);
  observer.observe(root.querySelector('.works-heading'));
  cards.forEach((card) => observer.observe(card.querySelector('.process-copy')));
  refresh();

  return () => {
    observer.disconnect();
    cleanups.forEach((cleanup) => cleanup());
    lenis.off('scroll', updateControls);
    horizontal?.destroy();
    scrollbar?.destroy();
    root.classList.remove('is-horizontal');
    controls.hidden = true;
  };
}
